<script setup lang="ts">
import type {
  ColDef,
  GridApi,
  GridReadyEvent,
  RowSelectionOptions,
} from "ag-grid-community";
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import AppGrid from "../../components/AppGrid.vue";
import { formatCalendarDateForDisplay } from "../../utils/dateFormatting.ts";
import type { BulkOrderCreationRow } from "./bulkOrderCreationMock.ts";

const props = defineProps<{
  rows: BulkOrderCreationRow[];
  isOpen: boolean;
  modalOpen: boolean;
  moveDisabled?: boolean;
  selectedSkus: string[];
  idPrefix?: string | undefined;
}>();

function scopedId(id: string): string {
  return props.idPrefix ? `${props.idPrefix}-${id}` : id;
}

const emit = defineEmits<{
  (event: "close"): void;
  (event: "move" | "selected-skus-change", skus: string[]): void;
}>();

const sidebarRef = ref<HTMLElement | null>(null);
const sidebarWidth = ref(660);
const gridApi = ref<GridApi<BulkOrderCreationRow>>();
let resizing: { pointerId: number; startX: number; startWidth: number } | null =
  null;
let triggerBeforeOpen: HTMLElement | null = null;

function setSidebarWidth(width: number): void {
  const viewportWidth =
    document.documentElement.clientWidth || window.innerWidth;
  sidebarWidth.value = Math.min(viewportWidth, Math.max(400, width));
}

function startResize(event: PointerEvent): void {
  if (event.button !== 0) return;
  event.preventDefault();
  resizing = {
    pointerId: event.pointerId,
    startX: event.clientX,
    startWidth:
      sidebarRef.value?.getBoundingClientRect().width ?? sidebarWidth.value,
  };
  document.addEventListener("pointermove", resize);
  document.addEventListener("pointerup", endResize);
  document.addEventListener("pointercancel", endResize);
}

function resize(event: PointerEvent): void {
  if (event.pointerId !== resizing?.pointerId) return;
  event.preventDefault();
  setSidebarWidth(resizing.startWidth + resizing.startX - event.clientX);
}

function endResize(): void {
  resizing = null;
  document.removeEventListener("pointermove", resize);
  document.removeEventListener("pointerup", endResize);
  document.removeEventListener("pointercancel", endResize);
}

function resizeWithKeyboard(event: KeyboardEvent): void {
  if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
  event.preventDefault();
  setSidebarWidth(sidebarWidth.value + (event.key === "ArrowLeft" ? 32 : -32));
}

function onViewportResize(): void {
  endResize();
  setSidebarWidth(sidebarWidth.value);
}

function renderSkuCell(parameters: { value: string }): HTMLElement {
  const strong = document.createElement("strong");
  strong.textContent = parameters.value;
  return strong;
}

const columnDefs: ColDef<BulkOrderCreationRow>[] = [
  {
    field: "sku",
    headerName: "SKU",
    width: 108,
    minWidth: 100,
    cellRenderer: renderSkuCell,
  },
  {
    field: "description",
    headerName: "Description",
    flex: 1,
    minWidth: 140,
  },
  {
    field: "shipDate",
    headerName: "Ship Date",
    width: 94,
    minWidth: 88,
    valueFormatter: ({ value }) =>
      formatCalendarDateForDisplay(typeof value === "string" ? value : ""),
  },
  {
    field: "shipToWarehouse",
    headerName: "Warehouse",
    width: 124,
    minWidth: 112,
  },
  { field: "requestedUnits", headerName: "Qty", width: 72, minWidth: 64 },
];

const defaultColDef: ColDef = {
  sortable: true,
  filter: false,
  resizable: true,
};

const rowSelection: RowSelectionOptions = {
  mode: "multiRow",
  checkboxes: true,
  headerCheckbox: true,
  selectAll: "all",
  enableClickSelection: false,
};
const selectionColumnDef: ColDef = {
  headerName: "Select",
  width: 44,
  minWidth: 44,
  maxWidth: 44,
  pinned: "left",
};
const selectedRows = computed(() => {
  const selected = new Set(props.selectedSkus);
  return props.rows.filter((row) => selected.has(row.sku));
});

function getRowId(parameters: { data: unknown }): string {
  const data = parameters.data;
  if (
    typeof data !== "object" ||
    data === null ||
    !("sku" in data) ||
    typeof data.sku !== "string"
  ) {
    throw new TypeError("Leftover inventory grid rows require a SKU.");
  }
  return data.sku;
}

function syncGridSelection(): void {
  if (!gridApi.value) return;
  const selected = new Set(props.selectedSkus);
  gridApi.value.forEachNode((node) => {
    if (node.data) node.setSelected(selected.has(node.data.sku));
  });
}

function onGridReady(event: GridReadyEvent<BulkOrderCreationRow>): void {
  gridApi.value = event.api;
  syncGridSelection();
}

function onSelectionChanged(): void {
  if (!gridApi.value) return;
  const selectedSkus = gridApi.value
    .getSelectedRows()
    .map((row) => row.sku)
    .filter((sku) => props.rows.some((row) => row.sku === sku));
  emit("selected-skus-change", selectedSkus);
}

function onKeydown(event: KeyboardEvent): void {
  if (props.isOpen && !props.modalOpen && event.key === "Escape") {
    close(sidebarRef.value?.contains(event.target as Node) ?? false);
  }
}

function close(restoreFocus: boolean): void {
  const returnFocusTarget = triggerBeforeOpen;
  emit("close");
  if (restoreFocus && returnFocusTarget) {
    void nextTick(() => {
      returnFocusTarget.focus();
    });
  }
}

watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      triggerBeforeOpen =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      return;
    }

    triggerBeforeOpen = null;
    endResize();
  }
);

watch(
  () => props.rows.map((row) => row.sku).join("\u0000"),
  () => {
    const currentSkus = new Set(props.rows.map((row) => row.sku));
    const selectedSkus = props.selectedSkus.filter((sku) =>
      currentSkus.has(sku)
    );
    if (selectedSkus.length !== props.selectedSkus.length) {
      emit("selected-skus-change", selectedSkus);
    }
    void nextTick(syncGridSelection);
  }
);

watch(
  () => props.selectedSkus,
  () => void nextTick(syncGridSelection),
  { deep: true }
);

onMounted(() => {
  document.addEventListener("keydown", onKeydown, true);
  window.addEventListener("resize", onViewportResize);
  window.addEventListener("blur", endResize);
});

onUnmounted(() => {
  document.removeEventListener("keydown", onKeydown, true);
  window.removeEventListener("resize", onViewportResize);
  window.removeEventListener("blur", endResize);
  endResize();
});
</script>

<template>
  <Teleport to="body">
    <div class="leftover-inventory-sidebar-root">
      <Transition name="leftover-inventory-sidebar">
        <aside
          v-show="isOpen"
          :id="scopedId('leftover-inventory-sidebar')"
          ref="sidebarRef"
          class="leftover-inventory-sidebar-panel"
          :style="{ width: `${String(sidebarWidth)}px` }"
          :aria-hidden="!isOpen"
          :inert="modalOpen"
          :aria-labelledby="scopedId('leftover-inventory-sidebar-heading')"
        >
          <button
            :id="scopedId('leftover-inventory-move-button')"
            type="button"
            class="leftover-inventory-sidebar-resize"
            aria-label="Resize leftover inventory sidebar"
            title="Drag to resize, or use left and right arrow keys"
            @pointerdown="startResize"
            @keydown="resizeWithKeyboard"
          />
          <header class="leftover-inventory-sidebar-header">
            <h2 :id="scopedId('leftover-inventory-sidebar-heading')">
              Leftover Inventory
            </h2>
            <button
              type="button"
              class="leftover-inventory-sidebar-close"
              aria-label="Close leftover inventory"
              @click="close(true)"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </header>
          <div class="leftover-inventory-sidebar-body">
            <p class="leftover-inventory-sidebar-note">
              Quantities not currently allocated to a suggested PO remain
              available in the originating order.
            </p>
            <div class="leftover-inventory-sidebar-grid">
              <AppGrid
                v-if="isOpen"
                :id="scopedId('leftover-inventory-grid')"
                :column-defs="columnDefs"
                :row-data="rows"
                :default-col-def="defaultColDef"
                :row-selection="rowSelection"
                :selection-column-def="selectionColumnDef"
                :get-row-id="getRowId"
                :fill-container="true"
                :auto-size-columns="false"
                :default-row-height="24"
                :get-row-height="() => 24"
                :no-rows-overlay-component-params="{
                  getTitle: () => 'No leftover SKU lines.',
                }"
                @grid-ready="onGridReady"
                @selection-changed="onSelectionChanged"
              />
            </div>
          </div>
          <footer class="leftover-inventory-sidebar-footer">
            <button
              type="button"
              class="btn btn-primary"
              data-testid="leftover-inventory-move-button"
              :disabled="selectedRows.length === 0 || moveDisabled"
              @click="
                emit(
                  'move',
                  selectedRows.map((row) => row.sku)
                )
              "
            >
              Move selected SKUs
            </button>
          </footer>
        </aside>
      </Transition>
    </div>
  </Teleport>
</template>

<style scoped>
.leftover-inventory-sidebar-root {
  position: fixed;
  z-index: 9999;
  inset: 0;
  pointer-events: none;
}

.leftover-inventory-sidebar-panel {
  position: absolute;
  inset-block: 0;
  right: 0;
  display: flex;
  max-width: 100vw;
  height: 100vh;
  flex-direction: column;
  border-left: 1px solid var(--dashboard-border-strong, #d1d5db);
  background-color: var(--dashboard-surface-elevated, #fff);
  box-shadow: var(--dashboard-shadow-strong, 0 18px 40px rgb(0 0 0 / 18%));
  pointer-events: auto;
}

.leftover-inventory-sidebar-header {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  border-bottom: 1px solid var(--dashboard-divider, #e5e7eb);
  padding: 0.75rem;
}

.leftover-inventory-sidebar-header h2 {
  margin: 0;
  color: var(--dashboard-text-primary, #172033);
  font-size: 1rem;
  font-weight: 600;
}

.leftover-inventory-sidebar-close {
  display: inline-flex;
  width: 2.25rem;
  height: 2.25rem;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  border-radius: 0.75rem;
  background: transparent;
  color: var(--dashboard-text-secondary, #5d6878);
  cursor: pointer;
}

.leftover-inventory-sidebar-close:hover {
  border-color: var(--dashboard-border-strong, #d1d5db);
  color: var(--dashboard-text-primary, #172033);
}

.leftover-inventory-sidebar-body {
  display: flex;
  min-height: 0;
  flex: 1;
  flex-direction: column;
  gap: 0.75rem;
  overflow: auto;
  overscroll-behavior: contain;
  padding: 0.75rem;
}

.leftover-inventory-sidebar-footer {
  display: flex;
  flex: 0 0 auto;
  justify-content: flex-end;
  border-top: 1px solid var(--dashboard-divider, #e5e7eb);
  padding: 0.75rem;
}

.leftover-inventory-sidebar-note {
  margin: 0;
  color: var(--text-secondary, #5d6878);
  font-size: 0.8rem;
}

.leftover-inventory-sidebar-grid {
  min-height: 12rem;
  flex: 1;
}

.leftover-inventory-sidebar-resize {
  position: absolute;
  z-index: 1;
  inset-block: 0;
  left: -4px;
  width: 8px;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: ew-resize;
  touch-action: none;
}

.leftover-inventory-sidebar-resize:hover,
.leftover-inventory-sidebar-resize:focus-visible {
  background: var(--action-primary, #1261a0);
}

/* Match the compact Suggested PO table without changing other AppGrid users. */
/* stylelint-disable selector-pseudo-class-no-unknown */
.leftover-inventory-sidebar-grid :deep(.ag-cell) {
  padding-inline: 0.1rem;
  font-size: 12px;
}

.leftover-inventory-sidebar-grid :deep(.ag-header-cell) {
  padding-inline: 0.1rem;
}

.leftover-inventory-sidebar-grid :deep(.ag-header-cell-text) {
  font-size: 12px;
  letter-spacing: 0.04em;
}
/* stylelint-enable selector-pseudo-class-no-unknown */

.leftover-inventory-sidebar-enter-active,
.leftover-inventory-sidebar-leave-active {
  transition: transform 0.2s ease-in-out;
}

.leftover-inventory-sidebar-enter-from,
.leftover-inventory-sidebar-leave-to {
  transform: translateX(100%);
}

@media (prefers-reduced-motion: reduce) {
  .leftover-inventory-sidebar-enter-active,
  .leftover-inventory-sidebar-leave-active {
    transition: none;
  }
}
</style>
